import { motion } from "framer-motion";

const EASE = [0.16, 1, 0.3, 1];

const fadeUp = {
  initial: { opacity: 0, y: 28 },
  whileInView: { opacity: 1, y: 0 },
  viewport: { once: true, margin: "-60px" },
};

const IMG = (id) =>
  `https://images.pexels.com/photos/${id}/pexels-photo-${id}.jpeg?auto=compress&cs=tinysrgb&w=1400`;

const MAIN = [
  {
    id: "players",
    title: "Players",
    body: "Build a permanent football identity. Preserve your matches, achievements, teams and football journey in one place.",
    img: IMG(4622751),
    alt: "Young player performing a bicycle kick on a grassroots pitch in Kolkata",
  },
  {
    id: "communities",
    title: "Football Communities & Clubs",
    body: "Bring your players, teams, matches and history together while keeping your community independent.",
    img: IMG(1375148),
    alt: "Local team competing during a community football match in India",
  },
  {
    id: "academies",
    title: "Academies",
    body: "Preserve player development, showcase progress and create a trusted record of every football journey.",
    img: IMG(33390340),
    alt: "Youth players training on a dusty community field in Kolkata",
  },
];

const SECONDARY = [
  {
    id: "organisers",
    title: "Organisers",
    img: IMG(15979274),
    alt: "Community football match on a misty field in Kolkata",
  },
  {
    id: "referees",
    title: "Referees",
    img: IMG(1375149),
    alt: "Players training on an outdoor grassroots pitch in India",
  },
];

function handleMove(e) {
  const el = e.currentTarget;
  const r = el.getBoundingClientRect();
  el.style.setProperty("--mx", `${e.clientX - r.left}px`);
  el.style.setProperty("--my", `${e.clientY - r.top}px`);
}

function MainTile({ tile, delay }) {
  return (
    <motion.article
      {...fadeUp}
      transition={{ duration: 0.9, ease: EASE, delay }}
      onMouseMove={handleMove}
      data-testid={`serve-tile-${tile.id}`}
      className="group relative aspect-[3/4] overflow-hidden border border-[#2A2A2A] transition-[border-color,transform] duration-500 hover:border-[#1F7A4D]/50 hover:-translate-y-1"
    >
      <img
        src={tile.img}
        alt={tile.alt}
        loading="lazy"
        className="absolute inset-0 h-full w-full object-cover object-center transition-transform duration-[1100ms] ease-out group-hover:scale-[1.07]"
      />
      {/* Readability overlay */}
      <div className="absolute inset-0 bg-gradient-to-t from-[#0B0B0B] via-[#0B0B0B]/45 to-[#0B0B0B]/15" />
      {/* Cursor-follow glow */}
      <div
        className="pointer-events-none absolute inset-0 opacity-0 transition-opacity duration-300 group-hover:opacity-100"
        style={{
          background:
            "radial-gradient(260px circle at var(--mx, 50%) var(--my, 50%), rgba(31,122,77,0.30), transparent 68%)",
        }}
      />
      {/* Content */}
      <div className="absolute inset-x-0 bottom-0 p-6 md:p-8">
        <h3 className="font-display uppercase text-white text-3xl md:text-4xl leading-[0.95] tracking-[0.01em]">
          {tile.title}
        </h3>
        <p className="mt-4 max-w-[38ch] font-body text-sm md:text-base font-light leading-relaxed text-[#B3B3B3]">
          {tile.body}
        </p>
      </div>
    </motion.article>
  );
}

function SecondaryTile({ tile, delay }) {
  return (
    <motion.article
      {...fadeUp}
      transition={{ duration: 0.9, ease: EASE, delay }}
      onMouseMove={handleMove}
      data-testid={`serve-tile-${tile.id}`}
      className="group relative aspect-[16/9] overflow-hidden border border-[#2A2A2A] transition-[border-color,transform] duration-500 hover:border-[#1F7A4D]/50 hover:-translate-y-1"
    >
      <img
        src={tile.img}
        alt={tile.alt}
        loading="lazy"
        className="absolute inset-0 h-full w-full object-cover object-center opacity-70 transition-transform duration-[1100ms] ease-out group-hover:scale-[1.07]"
      />
      <div className="absolute inset-0 bg-[#0B0B0B]/55" />
      <div
        className="pointer-events-none absolute inset-0 opacity-0 transition-opacity duration-300 group-hover:opacity-100"
        style={{
          background:
            "radial-gradient(260px circle at var(--mx, 50%) var(--my, 50%), rgba(31,122,77,0.28), transparent 68%)",
        }}
      />
      <div className="absolute inset-x-0 bottom-0 flex items-end justify-between p-6 md:p-8">
        <h3 className="font-display uppercase text-white text-3xl md:text-4xl leading-none tracking-[0.02em]">
          {tile.title}
        </h3>
        <span className="font-body text-xs font-medium tracking-[0.28em] uppercase text-[#1F7A4D]">
          Coming Soon
        </span>
      </div>
    </motion.article>
  );
}

export default function WhoWeServe() {
  return (
    <section
      id="who-we-serve"
      data-testid="who-we-serve"
      className="relative z-20 border-t border-[#2A2A2A] bg-[#0B0B0B] py-20 md:py-28"
    >
      <div className="max-w-[1400px] mx-auto px-6 md:px-12 lg:px-16">
        {/* Header: headline left, supporting copy right */}
        <div className="flex flex-col gap-8 lg:flex-row lg:items-end lg:justify-between lg:gap-16">
          <div className="max-w-[820px]">
            <motion.span
              {...fadeUp}
              transition={{ duration: 0.8, ease: EASE }}
              className="mb-7 flex items-center gap-3"
            >
              <span className="h-2 w-2 rounded-full bg-[#1F7A4D]" />
              <span className="font-body text-xs md:text-sm font-medium tracking-[0.3em] uppercase text-[#B3B3B3]">
                Who We Serve
              </span>
            </motion.span>
            <motion.h2
              {...fadeUp}
              transition={{ duration: 0.9, ease: EASE, delay: 0.05 }}
              data-testid="who-we-serve-heading"
              className="font-display uppercase text-white text-5xl md:text-6xl lg:text-7xl leading-[0.88] tracking-[0.01em]"
            >
              One Platform.
              <br />
              The Whole Football Ecosystem.
            </motion.h2>
          </div>
          <motion.p
            {...fadeUp}
            transition={{ duration: 0.9, ease: EASE, delay: 0.12 }}
            className="max-w-[360px] font-body text-lg font-light leading-relaxed text-[#B3B3B3] lg:text-right"
          >
            Built for the players, communities and academies growing grassroots
            football.
          </motion.p>
        </div>

        {/* Main 3-column tiles */}
        <div className="mt-16 md:mt-20 grid grid-cols-1 gap-5 md:grid-cols-3 md:gap-6">
          {MAIN.map((tile, i) => (
            <MainTile key={tile.id} tile={tile} delay={i * 0.1} />
          ))}
        </div>

        {/* Secondary tiles */}
        <div className="mt-5 md:mt-6 grid grid-cols-1 gap-5 md:grid-cols-2 md:gap-6">
          {SECONDARY.map((tile, i) => (
            <SecondaryTile key={tile.id} tile={tile} delay={i * 0.1} />
          ))}
        </div>
      </div>
    </section>
  );
}
